import useApiRequest from "@/lib/hooks/use-request";
import { formatDateTime } from "@/lib/hooks/utils";
import { DealDetailsResponseDto, DealHistoryResponseDto, DealHistoryType } from "@/lib/interfaces/deals";
import { Paginated } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Textarea } from "@/ui-components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/ui-components/ui/tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar, ChevronDown, ChevronRight, MessageSquare, User } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useUser } from "@/lib/hooks/use-user";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";

const DealHistory = ({
  item,
  setReload,
}: {
  item: DealDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const [notes, setNotes] = useState<DealHistoryResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const getNotes = useCallback(async () => {
    setIsLoading(true);
    
    // First, get stored history from localStorage
    const storedHistory = localStorage.getItem(`deal_history_${item._id}`);
    const localHistory = storedHistory ? JSON.parse(storedHistory) : [];
    
    const response = await apiRequest<Paginated<DealHistoryResponseDto>>(`deals/history/${item._id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      // Merge API data with local storage data
      const apiHistory = response.data.docs;
      const mergedHistory = [...localHistory, ...apiHistory];
      
      // Remove duplicates based on description and author (createdAt may differ between local and API)
      const uniqueHistory = mergedHistory.filter((entry, index, self) =>
        index === self.findIndex((h) =>
          h.historyNoteContent?.description === entry.historyNoteContent?.description &&
          (h.belongsToUserFullName || "") === (entry.belongsToUserFullName || "")
        )
      );
      
      setNotes(uniqueHistory);
    } else {
      // If API fails, at least show local history
      setNotes(localHistory);
    }
  }, [apiRequest, item._id]);

  useEffect(() => {
    getNotes();
  }, [getNotes]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <DealForm item={item} setReload={setReload} onNoteCreated={getNotes} />
      </div>
      
      <div className="border-t pt-4">
        <div className="flex items-center mb-4">
          <button
            className="flex items-center text-sm font-medium text-gray-700 hover:text-primary"
            onClick={toggleExpanded}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
            Verlauf
          </button>
        </div>

        {isExpanded && (
          <div className="flex-1 overflow-y-auto">
            <DataFetchParent
              isLoading={isLoading}
              data={notes}
              renderElement={(loadedNotes: DealHistoryResponseDto[]) => (
                <div className="relative pl-6 grid gap-4 grid-cols-[min-content_1fr] pr-2">
                  {loadedNotes.length > 0 ? (
                    <>
                      {loadedNotes.map((note, index) => (
                        <HistoryItem
                          key={note._id}
                          note={note}
                          isLast={index === loadedNotes.length - 1}
                          isFirst={index === 0}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="py-4 text-center text-gray-500 text-sm">Keine Einträge vorhanden</div>
                  )}
                </div>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface HistoryItemProps {
  note: DealHistoryResponseDto;
  isLast: boolean;
  isFirst: boolean;
}

const HistoryItem = ({ note, isLast, isFirst }: HistoryItemProps) => {
  // Determine icon based on note type
  const getIcon = () => {
    switch (note.historyType) {  // Changed from note.type to note.historyType
      case DealHistoryType.HISTORY_NOTE:
      case DealHistoryType.NOTE:
        return <MessageSquare className="h-3 w-3" />;
      case DealHistoryType.ACTIVITY:
        return <Calendar className="h-3 w-3" />;
      default:
        return <MessageSquare className="h-3 w-3" />;
    }
  };

  // Format date from the createdAt timestamp
  // Handle both Date objects and ISO strings
  let formattedDate = 'Datum nicht verfügbar';
  if (note.createdAt) {
    try {
      const date = typeof note.createdAt === 'string' ? new Date(note.createdAt) : note.createdAt;
      if (!isNaN(date.getTime())) {
        formattedDate = formatDateTime(date.toISOString());
      }
    } catch (error) {
      console.warn('Error formatting date:', note.createdAt, error);
    }
  }

  return (
    <>
      {/* Timeline bubble */}
      <div className="w-full relative flex justify-center">
        <div
          className={`flex items-center justify-center ${
            isFirst || isLast ? "w-5 h-5" : "w-8 h-8"
          } rounded-full bg-white border border-gray-300 z-10`}
        >
          <div className="flex items-center justify-center w-3.5 h-3.5 rounded-full">{getIcon()}</div>
        </div>
        {!isLast && (
          <div className="absolute border-l-2 border-dashed border-gray-200 top-0 mt-5 w-0.5 h-full bottom-0" />
        )}
      </div>

      <div className="w-full">
        <div className="bg-white rounded-md p-3 border border-gray-200 shadow-sm">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="flex items-center mr-2">
                      <User className="h-3.5 w-3.5 mr-1 text-gray-500" />
                      <span className="text-xs font-medium">{note.belongsToUserFullName}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Erstellt von {note.belongsToUserFullName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <span className="text-xs text-gray-500">{formattedDate}</span>
          </div>

          {note.historyNoteContent && (
            <div className="text-sm text-gray-800 whitespace-pre-wrap">{note.historyNoteContent.description}</div>
          )}
        </div>
      </div>
    </>
  );
};

const DealForm = ({
  item,
  setReload,
  onNoteCreated,
}: {
  item: DealDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
  onNoteCreated: () => void;
}) => {
  const { apiRequest } = useApiRequest();
  const { user } = useUser();
  const schema = z.object({
    description: z.string().min(1),
  });

  type DealFormSchema = z.infer<typeof schema>;

  const form = useForm<DealFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: "",
    },
  });

  const createNote = async (data: DealFormSchema) => {
    try {
      await apiRequest<DealHistoryResponseDto>(`deals/history/${item._id}/notes`, "POST", {
        body: { description: data.description },
      });
      
      // Show success message
      toast.success('Kommentar erfolgreich erstellt');
      
      // Reset form
      form.reset();
      
      // Optimistically add to local history with current timestamp so the date is shown immediately
      const optimisticEntry: DealHistoryResponseDto = {
        _id: `local_${Date.now()}`,
        belongsToUserId: user?._id || "unknown",
        historyType: DealHistoryType.NOTE,
        createdAt: new Date().toISOString(),
        belongsToUserFullName: user ? `${user.firstName} ${user.lastName}` : "Unbekannter Benutzer",
        historyNoteContent: {
          _id: `note_${Date.now()}`,
          description: data.description,
        },
      } as any;

      const existingHistory = JSON.parse(localStorage.getItem(`deal_history_${item._id}`) || '[]');
      localStorage.setItem(`deal_history_${item._id}`, JSON.stringify([optimisticEntry, ...existingHistory]));

      // Trigger immediate reload
      setReload((prev) => !prev);
      
      // Also refresh the notes immediately
      onNoteCreated();
    } catch (error) {
      toast.error('Fehler beim Erstellen des Kommentars');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(createNote)}>
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Kommentar schreiben</FormLabel>
              <FormControl>
                <Textarea placeholder="Schreiben Sie einen Kommentar..." className="resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-row gap-2 mt-3 justify-end">
          <Button type="submit" size="sm">
            Erstellen
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default DealHistory;
